// /src/data/books/Cryptonomics/cap7.js
const CRYPTONOMICS_CAP7 = {
  id: "cryptonomics-cap7",
  slug: "moneda-digital-vs-criptomoneda",
  title: "Capítulo 7 — Moneda Digital vs. Criptomoneda: La Delgada Línea",
  language: "es",
  authors: ["Autor no especificado"],
  description:
    "Analiza las diferencias estructurales entre moneda digital emitida por bancos centrales y criptomonedas descentralizadas. Expone las ventajas, vulnerabilidades y el papel de la escasez programada en la economía contemporánea.",
  tags: [
    "moneda digital",
    "criptomoneda",
    "escasez",
    "minado",
    "banco central",
    "blockchain",
    "criptografía"
  ],
  sections: [
    {
      id: "introduccion",
      heading: "La delgada línea entre moneda digital y criptomoneda",
      text: `En este capítulo se aborda la diferencia fundamental entre dos conceptos que suelen confundirse: **moneda digital** y **criptomoneda**. 
Ambas comparten una naturaleza intangible y la capacidad de permitir transacciones instantáneas a nivel global; sin embargo, difieren radicalmente en su mecanismo de emisión, control y seguridad.

La **moneda digital** es emitida por bancos centrales o entidades financieras bajo una política monetaria definida; su escasez es **administrada** y controlada por la autoridad emisora.  
Por el contrario, la **criptomoneda** nace de un sistema distribuido donde la creación de nuevas unidades depende del **minado o validación descentralizada**, mediante consenso computacional.  
Esto marca la frontera entre **confianza institucional** y **confianza algorítmica**, base del paradigma de las finanzas descentralizadas.`
    },
    {
      id: "comparativa-estructural",
      heading: "Comparativa estructural entre ambas",
      table: {
        columns: ["Característica", "Moneda Digital", "Criptomoneda"],
        rows: [
          [
            "Emisión",
            "Centralizada, bajo control de un banco central o autoridad monetaria.",
            "Descentralizada, regulada por protocolos criptográficos y consenso de red."
          ],
          [
            "Escasez",
            "Artificial y ajustable por política económica.",
            "Programada y limitada por diseño (ej. 21 millones en Bitcoin)."
          ],
          [
            "Validación",
            "Institucional, basada en registros bancarios y supervisión regulatoria.",
            "Distribuida, validada por nodos y mineros independientes."
          ],
          [
            "Soporte físico",
            "Basada en pasivos del banco y respaldos legales.",
            "Basada en cómputo, energía y consenso criptográfico."
          ],
          [
            "Confianza",
            "Se deposita en el Estado y en la regulación.",
            "Se deposita en el algoritmo y en la comunidad que mantiene la red."
          ],
          [
            "Acceso",
            "Depende de instituciones financieras y cuentas bancarias.",
            "Solo requiere conexión a internet y una clave privada."
          ]
        ]
      }
    },
    {
      id: "beneficios-criptomonedas",
      heading: "Beneficios y fundamentos de las criptomonedas",
      text: `Desde la perspectiva de sus fundamentos técnicos —los **cryptonomics**—, las criptomonedas representan la versión más pura del dinero digital:
1. **Escasez no controlada por bancos**: La oferta monetaria está determinada por un algoritmo público y auditable.
2. **Emisión descentralizada**: Los nodos y validadores verifican cada transacción mediante consenso.
3. **Recompensa decreciente**: A medida que aumenta el uso y se reduce el suministro disponible, el costo computacional del minado sube, reforzando la escasez.
4. **Valor incremental**: La relación entre demanda creciente y oferta limitada tiende a generar apreciación en el tiempo.

Por estas razones, los defensores de la descentralización consideran a la criptomoneda una forma de valor superior, incluso frente al oro, combinando la escasez de los metales preciosos con la portabilidad y velocidad del dinero digital.`
    },
    {
      id: "puntos-de-vista",
      heading: "Puntos de vista del autor",
      list: [
        {
          title: "1. Escasez genuina",
          text: "Su valor se sostiene en la dificultad creciente del minado, a diferencia de la moneda centralizada, cuya emisión puede expandirse sin límite."
        },
        {
          title: "2. Beneficio operativo gratuito",
          text: "Los usuarios acceden a un sistema global de transacciones sin necesidad de bancos ni comisiones excesivas, siempre que mantengan custodia segura de sus claves."
        },
        {
          title: "3. Seguridad distribuida",
          text: "El consenso descentralizado y la criptografía garantizan que ninguna entidad única pueda alterar el historial de transacciones."
        },
        {
          title: "4. Limitaciones físicas y prácticas",
          text: "A diferencia del oro, las criptomonedas no poseen valor de uso ni forma física; dependen totalmente de la infraestructura digital."
        },
        {
          title: "5. Dependencia del internet global",
          text: "Sin conectividad, las criptomonedas se vuelven inoperantes. Una interrupción global —por catástrofe o regulación internacional— podría paralizar su circulación."
        },
        {
          title: "6. Riesgo cuántico",
          text: "La aparición de ordenadores cuánticos capaces de resolver hashes a velocidades exponenciales amenaza con concentrar el poder de minado y romper la criptografía actual."
        },
        {
          title: "7. Duda sobre la escasez real",
          text: "Aunque los algoritmos fijan límites teóricos, la proliferación de nuevas criptomonedas y forks plantea interrogantes sobre la escasez efectiva del ecosistema."
        }
      ]
    },
    {
      id: "reflexion-final",
      heading: "Reflexión final: el equilibrio entre control y libertad",
      text: `La moneda digital y la criptomoneda ofrecen al consumidor un beneficio común: **la capacidad de transferir valor a nivel mundial de manera instantánea**. 
La diferencia radica en quién ejerce el control: en la primera, el banco central; en la segunda, el algoritmo y la comunidad global. 

Mientras los bancos centrales se preparan para emitir sus propias **CBDC (Central Bank Digital Currencies)**, la criptomoneda continúa representando un experimento social y tecnológico que pone a prueba los límites de la confianza y la soberanía financiera.`
    }
  ]
};

export default CRYPTONOMICS_CAP7;
